// Rating-category definitions for the Aluminum Filler Selector.
//
// Transcribed from the legacy native apps so the web calculator matches what Hobart's welding
// engineers compare against:
//   - Android: app/src/main/java/com/hobartbrothers/weldapp/weldapp/
//              Aluminum_Result_Definitions_Fragment.java:36-63 (the `String html` literal)
//   - iOS:     WeldApp/Base.lproj/Main.storyboard, "Weld Metal Properties" scene (cross-check)
//
// Three defects in the legacy copy are corrected here:
//   1. "weld solidication"      -> "weld solidification"
//   2. "Any specified filler"   -> "Any specified filler"
//      (both are mangled "fi" ligatures from the original design document, present in both apps)
//   3. iOS truncated "COLOR MATCH AFTER ANODIZIN" -> "COLOR MATCH AFTER ANODIZING" (Android is
//      already correct)
//
// Everything else is verbatim, including the curly-quoted ratings, "MAXAL" vs "MAXAL®", the
// en-dashes in "Groove welds –" / "Fillet welds –", and the missing final period after
// "substantial reduction in weld performance".

// The ** footnote follows the panel supplied by Galen White (Senior Welding Engineer / Product
// Specialist, Hobart Aluminum) in the "missing note in filler metal app" thread of 2026-08-20,
// which is the reference the engineering team asked us to match. Note that it is a superset of the
// legacy apps' copy: the 4943 paragraph is identical to theirs, but the title-case heading, the
// "Filler Metal ..." sub-headings and the whole 4047 section exist only in Galen's version.
export const specialConsiderations = {
  heading: '**Special Filler Metal Considerations',
  sections: [
    {
      heading: 'Filler Metal 4943',
      paragraphs: [
        '4943 will provide substantially higher strength with comparable weldability and crack ' +
          'sensitivity when compared to 4043. This filler metal has an addition of Mg which ' +
          'provides it with higher as-welded strength without dependence on dilution from base ' +
          'material. It will also provide greater strength in components that are subjected to ' +
          'post weld heat treatment (solutionizing and/or precipitation hardening).',
      ],
    },
    {
      heading: 'Filler Metal 4047',
      paragraphs: [
        '4047 has a lower melting temperature, slightly higher shear strength, reduced ' +
          'distortion, higher fluidity and reduced sensitivity to termination cracking when ' +
          'compared to 4043. It is often selected for leak-tight joints and may also make a good ' +
          'substitute for 4043 when welding other types of thin sections.',
      ],
    },
  ],
};

export const definitions = [
  {
    heading: 'CRACK SENSITIVITY',
    paragraphs: [
      'The Probability of Hot Cracking - this rating is established through use of crack ' +
        'sensitivity curves (Developed by Alcoa) and the consideration of filler metal and base ' +
        'metal chemistry combinations. There are levels of various alloying elements within ' +
        'aluminum that have been identified as seriously affecting hot cracking susceptibility ' +
        'during weld solidification. This rating is primarily based on the probability of ' +
        'producing a weld outside these crack sensitive chemistry ranges.',
    ],
  },
  {
    heading: 'STRENGTH',
    paragraphs: [
      'Ratings are for fillet weld and groove weld strength in the as welded condition. Groove ' +
        'welds – Any specified filler metal with a rating can provide minimum transverse tensile ' +
        'strength in groove welds that will meet the as-welded strength of the base material. ' +
        'Fillet welds – Ratings provided are for fillet weld shear strength.',
    ],
  },
  {
    heading: 'DUCTILITY',
    paragraphs: [
      'This characteristic of the completed weld may be of consideration if forming operations ' +
        'are to be used on a completed weldment during fabrication.',
      'Note: Testing procedure requirements for guided bend tests may need to be adjusted to ' +
        'accommodate the varying ductility of filler metals (AWS D1.2).',
    ],
  },
  {
    heading: 'CORROSION RESISTANCE',
    paragraphs: [
      'This variable may be a consideration for some environmental conditions. The rating is ' +
        'based on exposure to fresh and salt water environments and is not associated with a ' +
        'specific chemical exposure. It gives an indication as to the possibility of galvanic ' +
        'corrosion due to the difference in the electrode potential between the base metal and ' +
        'the filler metal. For consideration for other environmental and chemical exposures ' +
        'contact MAXAL.',
    ],
  },
  {
    heading: 'ELEVATED TEMPERATURE SERVICE',
    paragraphs: [
      'This rating is based on the reaction of some filler metals when exposed to sustained ' +
        'elevated temperature: 150°F to 350°F (66°C to 180°C). If 5xxx series base metal or ' +
        'filler metal with more than 3% magnesium content are subjected to prolonged exposure to ' +
        'these temperatures, precipitate can form within them that is highly anodic to the ' +
        'aluminum-magnesium matrix. It is this continuous grain boundary network of precipitate ' +
        'that produces susceptibility to stress corrosion cracking (SCC) and the potential for ' +
        'premature component failure.',
    ],
  },
  {
    heading: 'COLOR MATCH AFTER ANODIZING',
    paragraphs: [
      'Base metal and filler metal color match after post-weld anodizing can be of major concern ' +
        'in cosmetic applications. Some filler metals closely match the base metal color after ' +
        'anodizing and others will react to the anodizing process by changing to a color very ' +
        'different to that of the base metal.',
    ],
  },
  {
    heading: 'POST WELD HEAT TREATMENT',
    paragraphs: [
      'This rating applies to the ability of a weld to respond to post-weld heat treatment in ' +
        'the form of solution heat treatment and artificial aging. An “A” rating indicates that ' +
        'the filler metal is heat treatable and will therefore respond to post weld heat ' +
        'treatment even without dilution of the base metal. A “B” rating indicates that the ' +
        'filler metal is not heat treatable. However, it may be used for applications requiring ' +
        'post weld heat treatment but with the understanding that the weld may or may not ' +
        'acquire substantial increase in strength dependent on the joint design, welding ' +
        'procedure, and resultant amount of dilution of base metal obtained during welding. A ' +
        '“C” rating requires consultation with MAXAL®. No rating indicates that the filler metal ' +
        'is not heat treatable and that it should not be used for applications requiring post ' +
        'weld heat treatment as it may result in substantial reduction in weld performance',
    ],
  },
  {
    heading: 'TOUGHNESS',
    paragraphs: [
      'This rating applies to the ability of an aluminum weldment to deform plastically in the ' +
        'presence of stress raisers without low-energy initiation and propagation of cracks. The ' +
        'most useful test data is from tear resistance testing expressed in unit propagation ' +
        'energy of measured crack lengths. In structural design, notch toughness is becoming ' +
        'more emphasized by designers to facilitate the ability to inspect highly stressed ' +
        'structures and find cracks in weldments before catastrophic failure occurs. It may also ' +
        'be a design consideration if fatigue and impact loading are factors directly associated ' +
        'with a weldment.',
    ],
  },
];
